import { AddRounded, RemoveRounded } from '@mui/icons-material'
import { Button, Grid, InputBase, MenuItem, Popover, Typography } from '@mui/material'
import React, { memo, ReactNode, useCallback, useMemo, useState } from 'react'
import { addDaysToTimestamp } from '../../util/tradepanel'
import { getNextValidTradingDay, getPreviousValidTradingDay } from '../../util/tradingCalendar'
import AnalyticsManager from '../../analytics/AnalyticsManager'
import { toDayStart } from '../../util/dateTime'
import { useSelector } from 'react-redux'
import { selectTradingCalendarDataByExchange } from '../../selectors/chart'
import { selectPrimarySecurity } from '../../selectors/home'

export type Props = {
    baseTimestamp: number,
    targetTimestamp: number
    nearestExpiry: number,
    setTargetDate: (targetDate: number) => void
}

const formatDateDisplay = (date: Date, numberOfDays: number): string => {
    const options: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short' }
    const dateString = date.toLocaleDateString('en-US', options)
    return `${dateString}`
}

const TargetDateInput = ({
    nearestExpiry,
    baseTimestamp,
    targetTimestamp,
    setTargetDate
}: Props) => {
    const [popupAnchorEl, setPopupAnchorEl] = useState<HTMLElement | null>(null);
    const startDate = toDayStart(baseTimestamp);
    const targetDate = toDayStart(targetTimestamp);
    const nearestExpiryDate = toDayStart(nearestExpiry);
    const { underlying } = useSelector(selectPrimarySecurity)
    const calendarEvents = useSelector(selectTradingCalendarDataByExchange(underlying?.exchange))
    const holidays = useMemo(() => {
        return new Set(calendarEvents?.holidays?.map((e) => e.date))
    }, [calendarEvents])

    const displayValue = useMemo(() => {
        const numberOfDays = (nearestExpiryDate.getTime() - targetDate.getTime())/(24*60*60000)
        return formatDateDisplay(new Date(targetDate), numberOfDays)
    }, [targetDate, nearestExpiryDate])

    const handleOpenPopover = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setPopupAnchorEl(event.currentTarget)
    }, [])

    const handleMinusTargetDays = useCallback(() => {
        AnalyticsManager.logEvent("Target Days Changed", { source: 'strategy-builder-submenu', type: 'decrement' })

        const prevTradingDay = getPreviousValidTradingDay(
            new Date(targetDate.getTime() - 24*60*60*1000), // Start from previous day
            calendarEvents
        );

        if (prevTradingDay && prevTradingDay.getTime() >= startDate.getTime()) {
            setTargetDate(prevTradingDay.getTime());
        }
    }, [calendarEvents, startDate, targetDate, setTargetDate])

    const handlePlusTargetDays = useCallback(() => {
        AnalyticsManager.logEvent("Target Days Changed", { source: 'strategy-builder-submenu', type: 'increment' })
        
        const nextTradingDay = getNextValidTradingDay(
            new Date(targetDate.getTime() + 24*60*60*1000), // Start from next day
            calendarEvents
        );

        if (nextTradingDay && nextTradingDay.getTime() <= nearestExpiryDate.getTime()) {
            setTargetDate(nextTradingDay.getTime());
        }
    }, [calendarEvents, nearestExpiryDate, targetDate, setTargetDate])

    const handleTargetDayClick = useCallback((date: number) => {
        setTargetDate(date);
        setPopupAnchorEl(null)
    }, [setTargetDate])

    const handlePopoverClose = useCallback(() => {
        setPopupAnchorEl(null)
    }, [])

    const menuItems = useMemo(() => {
        const numberOfDays = (nearestExpiryDate.getTime() - startDate.getTime())/(24*60*60000)
        const items: ReactNode[] = [];
        const timesList: number[] = []
        if(numberOfDays < 0) return <></>
        for (let i=0; i <= numberOfDays; i++) {
            const date = new Date(addDaysToTimestamp(startDate.getTime(), i));
            const currentDate = new Date()
            currentDate.setDate(currentDate.getDate() + i)
            const yyyymmdd = [
                currentDate.getFullYear(),
                String(currentDate.getMonth() + 1).padStart(2, '0'),
                String(currentDate.getDate()).padStart(2, '0')
            ].join('')
            const isWeekend = currentDate.getDay() === 0 || currentDate.getDay() === 6;
            const numberOfDaysToExpiry = (nearestExpiryDate.getTime() - date.getTime())/(24*60*60000)
            const displayName = formatDateDisplay(date, numberOfDaysToExpiry)

            if(!holidays.has(yyyymmdd) && !isWeekend) {
                timesList.push(date.getTime())
                items.push(
                    <MenuItem data-event={'Target Days Selected'} data-event-source={'strategy-builder-submenu'} key={i.toString()} onClick={handleTargetDayClick.bind(null, date.getTime())}>
                        {displayName}
                    </MenuItem>
                )
            }
        }
        if(!timesList.includes(targetDate.getTime())) {
            setTargetDate(timesList[0])
        }
        return <>{items}</>;
    }, [startDate, targetDate, nearestExpiryDate, handleTargetDayClick])

    const popover = useMemo(() => {
        return (
            <Popover
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center'
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}
                anchorEl={popupAnchorEl}
                open={Boolean(popupAnchorEl)}
                onClose={handlePopoverClose}
            >
                {menuItems}
            </Popover>
        )
    }, [popupAnchorEl, handlePopoverClose, menuItems, handleTargetDayClick])

    const targetDateText = useMemo(() => {
        return (
            <Typography sx={{ fontSize: '10px' }}>
                TARGET DATE
            </Typography>
        )
    }, [])

    const minusButton = useMemo(() => {
        return (
            <div className="order-buttons" style={{ width: '21px', cursor: 'pointer' }} onClick={handleMinusTargetDays}>
                <RemoveRounded className="trade-panel-edit-icons" />
            </div>)
    }, [handleMinusTargetDays])

    const targetDaysInputBase = useMemo(() => {
        return (<InputBase
            value={displayValue}
            className="order-custom-input"
            onClick={handleOpenPopover}
            readOnly
            sx={{
                height: '100%',
                textAlign: 'center',
                backgroundColor: 'none',
                cursor: 'pointer',
                width: '110px',
                fontSize: '14px',
                '& .MuiInputBase-input': { padding: '4px 0 5px' }
            }}
        />)
    }, [handleOpenPopover, displayValue])

    const plusButton = useMemo(() => {
        return (
            <div className="order-buttons" style={{ width: '21px', cursor: 'pointer' }} onClick={handlePlusTargetDays}>
                <AddRounded className="trade-panel-edit-icons" />
            </div>)
    }, [handlePlusTargetDays])


    return (
        <Grid item id="target-day-container" sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
            <Grid item xs={12} textAlign={'left'}>
                <Typography variant="caption" component="span" color={'text.secondary'} className="trade-panel-text">
                    {targetDateText}
                </Typography>
            </Grid>
            <Grid item xs={12} textAlign={'center'}>
                <Typography className="trade-panel-boldest-text" variant="body2" component="span" color="primaryText" fontSize={'small'} sx={{ fontSize: '10px' }}>
                    <Button variant="outlined" color={'inherit'} sx={{
                        height: '24px',
                        padding: 0,
                        display: 'flex',
                        flexDirection: 'row !important',
                        alignItems: 'start !important'
                    }}>
                        {minusButton}
                        {targetDaysInputBase}
                        {plusButton}
                    </Button>
                    {popover}
                </Typography>
            </Grid>
        </Grid>
    )
}

export default memo(TargetDateInput)
